import type { Maybe } from '@/helpers/types';
import type { RouterMeta } from '@/routing/config';
import type { RouteLocationNormalized, RouteLocationResolvedGeneric } from 'vue-router';
import type { GetUrlMetadataQuery } from '@/graphql/query/GetUrlMetadata.query';

import { CollectionType } from '@/enums/collection-type';
import { decodeRoutePath } from '@/helpers/route-path-helper';
import { Domain, isBrHost } from '@/routing/helpers/subdomain-helper';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Type alias for URL metadata from GraphQL, extended with routing store additions
export type UrlMetadata = GetUrlMetadataQuery['urlV2'] & {
	allHrefLangTags?: Array<{ href: string; hrefLang: string; locale: string }>;
};

export type ListsCollectionType =
	| CollectionType.MY_LISTS
	| CollectionType.PUBLIC_LISTS
	| CollectionType.TV_SHOW_TRACKING
	| CollectionType.MY_RENTALS;

export type RoutingState = {
	activeRoute: RouteLocationNormalized | null;
	// this is used to fix the lazy loading of components with tabs.
	// [tab]: true when activated once
	activatedTabs: Record<string, boolean>;
	lastActiveCollectionType: CollectionType | null;
	lastActiveListsCollectionType: ListsCollectionType | null;
	lastRoute: RouteLocationNormalized | null;
	lastUrlMetadata: UrlMetadata | null;
	pageType: string;
	scrollPositions: Partial<Record<RouterMeta['tab'], number>>;
	urlMetadata: UrlMetadata | null;
};

export const useRoutingStore = defineStore('routing', () => {
	const activeDomain = ref<Domain>(Domain.MAIN);
	// BR_SUBDOMAIN
	// Whether the literal hostname the browser/crawler is actually looking at is the BR public
	// hostname (e.g. 'br.justwatch.com'), independent of `activeDomain` — used only for building
	// absolute URLs (canonical, hreflang, analytics) that must reflect a public hostname not
	// modeled by the `Domain` enum. We deliberately don't expose the raw hostname here so there's
	// no generic string for routing/content-scoping decisions to reach for instead of `activeDomain`.
	const isBrPublicHost = ref<boolean>(false);
	// BR_SUBDOMAIN
	const activeRoute = ref<RouteLocationNormalized | null>(null);
	// this is used to fix the lazy loading of components with tabs.
	// [tab]: true when activated once
	const activatedTabs = ref<Record<string, boolean>>({});

	const pageType = ref('VIEW_HOME'); // default url

	const lastActiveCollectionTypeState = ref<CollectionType | null>(null);

	const lastActiveListsCollectionType = ref<ListsCollectionType | null>(null);

	const lastRoute = ref<RouteLocationNormalized | null>(null);

	const lastUrlMetadata = ref<UrlMetadata | null>(null);

	const scrollPositions = ref<Partial<Record<RouterMeta['tab'], number>>>({});

	const urlMetadataState = ref<UrlMetadata | null>(null);

	const activeCollectionType = computed<CollectionType | null>(
		() => (activeRoute.value?.meta?.collectionType ?? null) as CollectionType | null
	);

	const lastActiveCollectionType = computed(() => {
		const currentCollectionType = activeCollectionType.value;
		if (currentCollectionType) {
			return currentCollectionType;
		}

		if (lastActiveCollectionTypeState.value) {
			return lastActiveCollectionTypeState.value;
		}

		// This is a failsafe, it should never land here. Gotta investigate or rewrite this
		return CollectionType.GLOBAL;
	});

	const queryParams = computed<RouteLocationNormalized['query']>(() => {
		if (activeRoute.value) {
			return activeRoute.value.query;
		}

		return {};
	});

	const urlMetadata = computed<RoutingState['urlMetadata']>(() => {
		const urlMetadata = (urlMetadataState.value || {}) as UrlMetadata;
		const heading1 = urlMetadata.heading1;

		return {
			...urlMetadata,
			...{ heading1 },
		};
	});

	function setActiveDomain(domain: Domain) {
		activeDomain.value = domain;
	}
	// BR_SUBDOMAIN
	function setVisibleHost(host: string) {
		isBrPublicHost.value = isBrHost(host);
	}
	// BR_SUBDOMAIN

	function setUrlMetadata(metadata?: Maybe<UrlMetadata>) {
		// Save last URL Metadata first
		lastUrlMetadata.value = urlMetadataState.value;

		const metaRobots = metadata?.metaRobots ?? '';
		const hasNoIndex = metaRobots.includes('noindex');
		// make sure we don't include urls that have noindex
		const hrefLangTags = (metadata?.hrefLangTags || []).filter(tag => !tag.hasNoindex && !hasNoIndex);

		urlMetadataState.value = {
			...((metadata ?? {}) as UrlMetadata),
			hrefLangTags,
			allHrefLangTags: metadata?.hrefLangTags || [],
		};
	}

	async function setActiveRoute(newActiveRoute: RouteLocationNormalized | RouteLocationResolvedGeneric) {
		lastRoute.value = activeRoute.value ? { ...activeRoute.value } : null;

		// exclude 'matched' that causes circular dependency
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		const { matched, meta, ...route } = newActiveRoute;
		activeRoute.value = {
			...route,
			meta: { ...meta },
			path: decodeRoutePath(route.path),
		} as RouteLocationNormalized;

		// only set lastActiveCollectionType of the last route that had a collectionType
		if (meta?.collectionType && typeof meta.collectionType === 'string') {
			lastActiveCollectionTypeState.value = meta.collectionType as CollectionType;
		}

		if (typeof meta?.tab === 'string') {
			activatedTabs.value[meta?.tab] = true;
		}
	}

	function setLastActiveListsCollectionType(collectionType: ListsCollectionType) {
		lastActiveListsCollectionType.value = collectionType;
	}

	function setScrollPosition({ tab, y }: { tab: RouterMeta['tab']; y: number }) {
		if (tab) {
			scrollPositions.value = { ...scrollPositions.value, [tab]: y };
		}
	}

	return {
		activeRoute,
		activatedTabs,
		pageType,
		lastActiveCollectionTypeState,
		lastActiveListsCollectionType,
		lastRoute,
		lastUrlMetadata,
		scrollPositions,
		urlMetadataState,

		activeCollectionType,
		lastActiveCollectionType,
		queryParams,
		urlMetadata,
		activeDomain,
		// BR_SUBDOMAIN
		isBrPublicHost,
		// BR_SUBDOMAIN

		setUrlMetadata,
		setActiveDomain,
		// BR_SUBDOMAIN
		setVisibleHost,
		// BR_SUBDOMAIN
		setActiveRoute,
		setLastActiveListsCollectionType,
		setScrollPosition,
	};
});
